/**
 * PageSEO — per-page SEO meta injection via react-helmet-async.
 *
 * Renders unique <title>, <meta description>, canonical, Open Graph,
 * Twitter cards, and (optionally) a Course JSON-LD block into <head>
 * so every /course/:id and /lesson/:id ranks on its own topic instead
 * of sharing the site-wide index.html defaults.
 *
 * Mount `<HelmetProvider>` at the top of App.js (already done) and drop
 * `<PageSEO … />` at the top of any page component that has a title,
 * description, and — for courses/lessons — schema data.
 *
 * Never crashes the page: every field is optional; missing values fall
 * back to sensible site-wide defaults.
 */
import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE = 'https://freezedryacademy.com';
const DEFAULT_IMAGE = `${SITE}/brand-logo-full.png`;
const DEFAULT_DESC =
  'Expert-led freeze drying courses, batch tracking tools, an AI assistant, and a community for home and commercial operators.';

const truncate = (s, n = 155) => {
  if (!s) return '';
  const clean = String(s).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  return clean.length > n ? clean.slice(0, n - 1).trimEnd() + '…' : clean;
};

const PageSEO = ({
  title,
  description,
  path,        // canonical path, defaults to current pathname
  image,       // absolute or /-prefixed URL
  type = 'website', // 'website' | 'article' | 'course'
  schema,      // optional JSON-LD object (Course, LearningResource, etc.)
  noindex = false,
}) => {
  const fullTitle = title
    ? `${title} · Freeze Dry Academy`
    : 'Freeze Dry Academy — Freeze Drying Courses, Tools & Community';
  const desc = truncate(description || DEFAULT_DESC);
  const canonPath = path || (typeof window !== 'undefined' ? window.location.pathname : '/');
  const canonUrl = `${SITE}${canonPath}`;
  const img = image
    ? (image.startsWith('http') ? image : `${SITE}${image.startsWith('/') ? '' : '/'}${image}`)
    : DEFAULT_IMAGE;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonUrl} />
      {noindex && <meta name="robots" content="noindex, follow" />}

      {/* Open Graph */}
      <meta property="og:type"        content={type === 'course' ? 'article' : type} />
      <meta property="og:title"       content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url"         content={canonUrl} />
      <meta property="og:image"       content={img} />

      {/* Twitter */}
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:title"       content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image"       content={img} />

      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
};

// Convenience: build a schema.org Course JSON-LD block from a course record.
export const buildCourseSchema = (course) => {
  if (!course) return null;
  return {
    '@context':    'https://schema.org',
    '@type':       'Course',
    name:          course.title,
    description:   truncate(course.description || '', 500),
    provider: {
      '@type': 'EducationalOrganization',
      name:    'Freeze Dry Academy',
      url:     SITE,
    },
    ...(course.id ? { url: `${SITE}/course/${course.id}` } : {}),
    ...(course.duration_minutes ? { timeRequired: `PT${course.duration_minutes}M` } : {}),
    ...(course.tier ? { educationalLevel: course.tier } : {}),
    hasCourseInstance: {
      '@type':       'CourseInstance',
      courseMode:    'online',
      inLanguage:    'en',
    },
  };
};

// Convenience: schema.org LearningResource for individual lessons.
export const buildLessonSchema = (lesson) => {
  if (!lesson) return null;
  return {
    '@context':      'https://schema.org',
    '@type':         'LearningResource',
    name:            lesson.title,
    description:     truncate(lesson.description || lesson.short_description || '', 500),
    learningResourceType: 'Lesson',
    ...(lesson.id ? { url: `${SITE}/lesson/${lesson.id}` } : {}),
    ...(lesson.duration_minutes ? { timeRequired: `PT${lesson.duration_minutes}M` } : {}),
    inLanguage: 'en',
    provider: {
      '@type': 'EducationalOrganization',
      name:    'Freeze Dry Academy',
      url:     SITE,
    },
  };
};

// Convenience: schema.org Article for blog posts. Google Discover
// reads `datePublished`, `dateModified`, `headline`, `image`, and
// `author` for editorial ranking, so we fill all five when available.
export const buildArticleSchema = (post) => {
  if (!post) return null;
  return {
    '@context':     'https://schema.org',
    '@type':        'Article',
    headline:       post.title,
    description:    truncate(post.excerpt || post.subtitle || '', 500),
    ...(post.hero_image_url ? { image: post.hero_image_url } : {}),
    datePublished:  post.published_at || post.created_at,
    dateModified:   post.updated_at || post.published_at || post.created_at,
    author: {
      '@type': 'Person',
      name:    post.author_name || 'Freeze Dry Academy',
      ...(post.author_avatar_url ? { image: post.author_avatar_url } : {}),
    },
    publisher: {
      '@type': 'Organization',
      name:    'Freeze Dry Academy',
      url:     SITE,
      logo:    { '@type': 'ImageObject', url: DEFAULT_IMAGE },
    },
    ...(post.url ? { mainEntityOfPage: { '@type': 'WebPage', '@id': post.url } } : {}),
    inLanguage: 'en',
  };
};

export default PageSEO;
